/**
 * Where each piece of paperwork stands, marked by hand.
 *
 * Moved from the old organization page's "People and paperwork" panel, which
 * was the only place these statuses could be set. The Agreements stage counted
 * a W-9 or a certificate of insurance as outstanding and offered nothing that
 * settled it; this is that control.
 *
 * The documents, their labels and the statuses are the backend's own (see
 * functions/src/billing/contacts.ts). `paperworkMatchesBackend.guard.test.ts`
 * holds the two copies together.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../../lib/callable';
import { formatDayUS } from '../../../shared/dates';
import page from '../../../shared/Page.module.css';
import form from '../../../shared/Form.module.css';
import { CompanyDocControls } from './CompanyDocControls';
import type { Person } from './types';
import { GroupCard } from '../../../shared/GroupCard';

/** The W-9 first on purpose: nothing moves until they can set us up as a supplier. */
const PAPERWORK = [
  { key: 'w9', label: 'W-9', detail: 'Always, and first: they cannot create you as a vendor without it.' },
  { key: 'coi', label: 'Certificate of insurance', detail: 'General liability and cyber/E&O. Many name themselves as additional insured.' },
  { key: 'orderForm', label: 'Order form', detail: 'Plan, locations, price, term and their PO number.' },
  { key: 'agreement', label: 'Agreement', detail: 'Larger systems send their own paper instead and expect redlines.' },
  { key: 'baa', label: 'Business associate agreement', detail: 'Only if they ask, and only once somebody has decided whether we are a business associate at all.' },
  { key: 'securityReview', label: 'Security questionnaire', detail: 'Theirs to send, ours to complete.' },
] as const;

const STATUSES = [
  { key: 'outstanding', label: 'Outstanding' },
  { key: 'sent', label: 'Sent to them' },
  { key: 'received', label: 'Received from them' },
  { key: 'executed', label: 'Executed' },
  { key: 'notNeeded', label: 'Not needed' },
] as const;

export interface PaperworkRow {
  status?: string;
  /** Where it went, when we know: "Emailed to ap@…", "Downloaded from their onboarding form". */
  reference?: string | null;
  at?: string | null;
  byEmail?: string | null;
}

export function PaperworkPanel({ orgId, paperwork, people = [] }: {
  orgId: string;
  paperwork: Record<string, PaperworkRow | undefined>;
  /** For the W-9's "Email it to". */
  people?: Person[];
}) {
  const queryClient = useQueryClient();
  const set = useMutation({
    mutationFn: (input: { key: string; status: string }) =>
      call('sysAdminSetOrgPaperwork', { orgId, ...input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding-activity', orgId] });
    },
  });

  return (
    <GroupCard id="paperwork-title" title="Paperwork">
      <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-2)' }}>
        Mark each one as it moves. A document signed through DocuSign is marked for you.
      </p>
      {set.error && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
          {isCallFailure(set.error) ? set.error.message : 'That status did not save.'}
        </div>
      )}
      <ul className={page.list} aria-label="Paperwork">
        {PAPERWORK.map((doc) => {
          const row = paperwork[doc.key];
          const status = row?.status ?? 'outstanding';
          const label = STATUSES.find((s) => s.key === status)?.label ?? status;
          return (
            <li key={doc.key} className={page.listItem} style={doc.key === 'w9' || doc.key === 'coi' ? { flexWrap: 'wrap' } : undefined}>
              <div style={{ minWidth: 0 }}>
                <div className={page.itemName}>{doc.label}</div>
                <div className={page.itemMeta}>
                  {row?.status
                    ? `${label}${row.reference && !row.reference.startsWith('DocuSign') ? `: ${row.reference.charAt(0).toLowerCase()}${row.reference.slice(1)}` : ''}${row.byEmail ? ` by ${row.byEmail}` : ''}${row.at ? ` on ${formatDayUS(row.at)}` : ''}.`
                    : doc.detail}
                </div>
              </div>
              <select
                id={`paper-${doc.key}`}
                className={form.select}
                style={{ maxWidth: 200 }}
                aria-label={`Status of ${doc.label}`}
                value={status}
                disabled={set.isPending}
                onChange={(e) => set.mutate({ key: doc.key, status: e.target.value })}
              >
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              {(doc.key === 'w9' || doc.key === 'coi') && <CompanyDocControls kind={doc.key} orgId={orgId} people={people} />}
            </li>
          );
        })}
      </ul>
    </GroupCard>
  );
}
